import { DisplayPriceGettingParams } from '../../../baseComponents/Displays/DisplayPrice'
import { TwinTableLink } from '../../../baseComponents/TwinTable'
import { TableComponentProps } from '../../../baseComponents/TwinTable/BaseScreenTable/types'
import { CustomRenderString, CustomRenderDate, EditRow } from '../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ColumnTableSchema } from '../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import { addDictionaryExtraLine } from '../../../utils/globals/dictionary'
import useTwinTranslation from '../../../utils/hooks/useTwinTranslation'
import { OnRowClick } from '../../../baseComponents/TwinTable/VirtualTableListing/types'
import locations from '../../../utils/globals/locations'
import { connect, ConnectedProps } from 'react-redux'
import withLoading from '../../../utils/hoc/withLoading'
import { AllReduxPayloads } from '../../../utils/reducers'
import { sortEmployeesByUser } from '../../../utils/globals/employees'
import { useMemo } from 'react'
import { getEmployees } from '../../../utils/reducers/getters'
import { EmployeesPayload } from '../../../utils/reducers/company/employees'
import { SellDeliveryNoteRefundModelType } from '@teinor/erp/types/company/order/sell/sellDeliveryNoteRefund'
import { faCheckCircle } from '@fortawesome/pro-light-svg-icons'
import { DisplayStatus } from '../../../baseComponents/Displays/DisplayStatus'

type TableSellDeliveryNoteRefundsProps = TableComponentProps & ReduxTableSellDeliveryNoteRefunds & {
    onRowClick?: OnRowClick
}

const TableSellDeliveryNoteRefunds: React.FC<TableSellDeliveryNoteRefundsProps> = ({ setTableInstance, onRowClick }) => {
    const { t } = useTwinTranslation()
    const memoEmployees = useMemo(() => addDictionaryExtraLine(JSON.parse(JSON.stringify(getEmployees() || {}))), [])
    const columns: ColumnTableSchema<SellDeliveryNoteRefundModelType> = [
        {
            id: 'full_assigned_number',
            dataKey: 'full_assigned_number',
            label: t('refund', 'Devolución'),
            sortable: true,
            customRender: (parameterValue, allRowData) => <CustomRenderString value={parameterValue} className={allRowData?.assigned_number ? '' : 'text-red-BA'} />,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('refund', 'Devolución') }
            },
            searchKey: 'SellDeliveryNoteRefund.full_assigned_number',
            minWidth: 140
        },
        {
            id: 'SellDeliveryNoteId',
            dataKey: 'SellDeliveryNoteId',
            label: t('sellDeliveryNoteShort', 'Albarán'),
            searchKey: 'SellDeliveryNote.full_assigned_number',
            sortable: true,
            customRender: (parameterValue, allRowData) => <CustomRenderString value={allRowData?.SellDeliveryNote?.full_assigned_number} />,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('sellDeliveryNoteShort', 'Albarán') }
            },
            minWidth: 120
        },
        {
            id: 'CustomerId',
            dataKey: 'CustomerId',
            label: t('customer', 'Cliente'),
            searchKey: 'Customer.name',
            minWidth: 130,
            customRender: (_parameterValue, allRowData) => <CustomRenderString value={allRowData?.Customer?.fiscal_name || allRowData?.Customer?.name || ''} />,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('customer', 'Cliente') }
            },
        },
        {
            id: 'CustomerFiscalNameId',
            dataKey: 'CustomerId',
            label: '',
            hideColumnByDefault: true,
            searchKey: 'Customer.fiscal_name',
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: '' }
            },
        },
        {
            id: 'status',
            dataKey: 'status',
            label: t('status', 'Estado'),
            sortable: true,
            minWidth: 130,
            customRender: (parameterValue) => <DisplayStatus icon={faCheckCircle} colorText='text-green-43' text={t('refunded', 'Devuelto')} />,
        },
        {
            id: 'WorkingCenterId',
            dataKey: 'WorkingCenterId',
            searchKey: 'WorkingCenter.name',
            label: t('soldBy', 'Vendido por'),
            customRender: (_parameterValue, allRowData) => <CustomRenderString value={allRowData?.WorkingCenter?.name || ''} />,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('workingCenter', 'Centro de trabajo') }
            },
            minWidth: 120
        },
        {
            id: 'processed_date',
            dataKey: 'processed_date',
            label: t('createdDate', 'Fecha de creación'),
            customRender: (parameterValue, allRowData) => <CustomRenderDate value={parameterValue || allRowData.createdAt} className={parameterValue ? '' : 'text-red-BA'} />,
            sortable: true,
            minWidth: 165
        },
        {
            id: 'custom_name',
            dataKey: 'custom_name',
            label: t('customName', 'Nombre personalizado'),
            sortable: true,
            minWidth: 130,
            hideColumnByDefault: true
        },
        {
            id: 'total_without_vat',
            dataKey: 'total_without_vat',
            label: t('totalWithoutVatS', 'B. imponible'),
            simpleLabel: t('totalWithoutVat', 'Base imponible'),
            sortable: true,
            minWidth: 130,
            customRender: (parameterValue, allRowData) => <DisplayPriceGettingParams value={parseFloat(parameterValue)} with_vat={false} CurrencyId={allRowData.CurrencyId} />,
        },
        {
            id: 'total_total',
            dataKey: 'total_total',
            label: t('total', 'Total'),
            sortable: true,
            minWidth: 100,
            customRender: (parameterValue, allRowData) => <DisplayPriceGettingParams value={parseFloat(parameterValue)} with_vat={allRowData.with_vat} CurrencyId={allRowData.CurrencyId} />,
        },
        {
            id: 'CustomerProjectId',
            dataKey: 'CustomerProjectId',
            label: t('project', 'Proyecto'),
            hideColumnByDefault: true,
            customRender: (_parameterValue, allRowData) => <CustomRenderString value={allRowData?.CustomerProject?.name || ''} />
        },
        {
            id: 'RepresentativeEmployeeId',
            dataKey: 'RepresentativeEmployeeId',
            label: t('representative', 'Representante'),
            searchKey: 'RepresentativeEmployee.fullname_short',
            customRender: (_parameterValue, allRowData) => {
                const employeeName = allRowData?.RepresentativeEmployee?.fullname_short
                return <CustomRenderString value={employeeName || ''} />
            },
            minWidth: 130,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSearchBar',
                extraComponentData: {
                    items: memoEmployees,
                    label: t('representative', 'Representante'),
                    sortItems: true,
                    sortFunction: sortEmployeesByUser
                }
            },
            searchSingleKey: 'RepresentativeEmployee.id'
        },
        {
            id: 'CreateEmployeeId',
            dataKey: 'CreateEmployeeId',
            label: t('madeBy', 'Realizado por'),
            hideColumnByDefault: true,
            searchKey: 'CreateEmployee.fullname_short',
            customRender: (_parameterValue, allRowData) => {
                const employeeName = allRowData?.CreateEmployee ? allRowData?.CreateEmployee?.fullname_short : allRowData?.ProcessEmployee?.fullname_short
                return <CustomRenderString value={employeeName || ''} />
            },
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSearchBar',
                extraComponentData: {
                    items: memoEmployees,
                    label: t('madeBy', 'Realizado por'),
                    sortItems: true,
                    sortFunction: sortEmployeesByUser
                }
            },
            searchSingleKey: 'CreateEmployee.id'
        },
        {
            id: 'CustomerProjectType',
            dataKey: 'id',
            hideColumnByDefault: true,
            label: t('projectType', 'Tipo de proyecto'),
            searchKey: 'CustomerProject.CustomerProjectType.name',
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('projectType', 'Tipo de proyecto') }
            },
            customRender: (parameterValue, allRowData) => <CustomRenderString value={allRowData?.CustomerProject?.CustomerProjectType?.name} />
        },
        {
            id: 'edit',
            dataKey: 'id',
            label: '',
            width: 50,
            simpleLabel: t('edit', 'Editar'),
            customRender: (parameterValue, allRowData) => <EditRow key={'edit' + parameterValue} {...{ parameterValue, allRowData }} />
        }
    ]
    return (
        <TwinTableLink createRowDataLink={[locations.sellDeliveryNoteRefundSingle, ['$id']]} name='sellDeliveryNoteRefunds' columns={columns} getDataFrom='/api/app/order/sell/sellDeliveryNoteRefund/getAllSellDeliveryNoteRefundsComplete' onRowClick={onRowClick} setTableInstance={setTableInstance} />
    )
}

const TableSellDeliveryNoteRefundsDispatch = {
    setEmployees: (payload: EmployeesPayload) => ({ type: 'CHANGE_EMPLOYEE', payload }),
}

export type ReduxTableSellDeliveryNoteRefunds = ConnectedProps<typeof tableSellDeliveryNoteRefundsConnect>
const mapTableSellDeliveryNoteRefundsConnector = (state: AllReduxPayloads) => ({ employees: state.employees })
const tableSellDeliveryNoteRefundsConnect = connect(mapTableSellDeliveryNoteRefundsConnector, TableSellDeliveryNoteRefundsDispatch)
const TableSellDeliveryNoteRefundsConnectLoading = withLoading(TableSellDeliveryNoteRefunds, [{ fetchUrl: '/api/app/employee/getAllEmployees', propName: 'employees', setFunctionName: 'setEmployees' }])
const TableSellDeliveryNoteRefundsConnect = tableSellDeliveryNoteRefundsConnect(TableSellDeliveryNoteRefundsConnectLoading)

export default TableSellDeliveryNoteRefundsConnect